/**
 * ProfileExtraDetailsSheet — co-located subcomponent of the Account · Profile tab.
 *
 * The "Edit extra details" side Sheet, re-skinned from the live ProfileSection
 * extra-details form. Same fields and the same UserService.updateUserProfile
 * write path; only the chrome is brought on-brand (side Sheet, genie tokens).
 */
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { UserService } from "@/services/users/UserService";

interface ProfileExtraDetailsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const LABEL = "block mb-1.5 text-[12.5px] font-semibold text-[#64748B]";
const INPUT =
  "h-[44px] w-full px-3.5 rounded-xl border border-genie-border bg-white text-[14px] text-[#222B45] focus:outline-none focus:border-genie-primary focus:ring-2 focus:ring-genie-primary/15 transition-all";

export const ProfileExtraDetailsSheet = ({
  open,
  onOpenChange,
}: ProfileExtraDetailsSheetProps) => {
  const { user, userProfile } = useAuth();
  const [saving, setSaving] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState("");
  const [phoneCountryCode, setPhoneCountryCode] = useState("");
  const [addressLine1, setAddressLine1] = useState("");
  const [addressLine2, setAddressLine2] = useState("");
  const [city, setCity] = useState("");
  const [stateProvince, setStateProvince] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [country, setCountry] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [jobTitle, setJobTitle] = useState("");

  useEffect(() => {
    if (userProfile) {
      setPhoneNumber(userProfile.phone_number || "");
      setPhoneCountryCode(userProfile.phone_country_code || "");
      setAddressLine1(userProfile.address_line1 || "");
      setAddressLine2(userProfile.address_line2 || "");
      setCity(userProfile.city || "");
      setStateProvince(userProfile.state_province || "");
      setPostalCode(userProfile.postal_code || "");
      setCountry(userProfile.country || "");
      setDateOfBirth(userProfile.date_of_birth || "");
      setCompanyName(userProfile.company_name || "");
      setJobTitle(userProfile.job_title || "");
    }
  }, [userProfile]);

  const handleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await UserService.updateUserProfile(user.id, {
        phone_number: phoneNumber || null,
        phone_country_code: phoneCountryCode || null,
        address_line1: addressLine1 || null,
        address_line2: addressLine2 || null,
        city: city || null,
        state_province: stateProvince || null,
        postal_code: postalCode || null,
        country: country || null,
        date_of_birth: dateOfBirth || null,
        company_name: companyName || null,
        job_title: jobTitle || null,
      });
      onOpenChange(false);
      toast.success("Extra details updated successfully.");
    } catch (err: any) {
      toast.error(`We couldn't save your details: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showClose={false}
        className="w-full sm:max-w-xl flex flex-col h-full p-0"
      >
        <div className="flex items-center justify-between border-b border-genie-border px-6 sm:px-8 py-6">
          <div>
            <h2 className="text-xl font-extrabold text-[#222B45] tracking-[-0.3px]">
              Extra details
            </h2>
            <p className="text-[13px] text-[#64748B] mt-1">
              Additional profile information for your account.
            </p>
          </div>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="text-gray-400 hover:text-[#222B45] text-2xl font-light transition-colors"
          >
            &times;
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 sm:px-8 py-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Phone number</label>
              <input
                type="tel"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                placeholder="e.g. +1234567890"
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>Phone country code</label>
              <input
                type="text"
                value={phoneCountryCode}
                onChange={(e) => setPhoneCountryCode(e.target.value)}
                placeholder="e.g. US, CA, UK"
                className={INPUT}
              />
            </div>
          </div>

          <div>
            <label className={LABEL}>Address line 1</label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Street address"
              className={INPUT}
            />
          </div>
          <div>
            <label className={LABEL}>Address line 2</label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Apartment, suite, etc."
              className={INPUT}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>City</label>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>State / province</label>
              <input
                type="text"
                value={stateProvince}
                onChange={(e) => setStateProvince(e.target.value)}
                className={INPUT}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Postal code</label>
              <input
                type="text"
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>Country</label>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className={INPUT}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={LABEL}>Date of birth</label>
              <input
                type="date"
                value={dateOfBirth}
                onChange={(e) => setDateOfBirth(e.target.value)}
                className={INPUT}
              />
            </div>
            <div>
              <label className={LABEL}>Company name</label>
              <input
                type="text"
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
                className={INPUT}
              />
            </div>
          </div>

          <div>
            <label className={LABEL}>Job title</label>
            <input
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              className={INPUT}
            />
          </div>

          {userProfile?.billing_currency && (
            <div>
              <label className={LABEL}>Billing currency</label>
              <input
                type="text"
                value={userProfile.billing_currency}
                disabled
                className={`${INPUT} bg-genie-background cursor-not-allowed text-gray-500`}
              />
            </div>
          )}
        </div>

        <div className="sticky bottom-0 border-t border-genie-border px-6 sm:px-8 py-4 flex justify-end gap-3 bg-white">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={saving}
            className="h-10 px-4 rounded-full border border-genie-border bg-white text-[13.5px] font-bold text-[#64748B] transition-all hover:opacity-90 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="h-10 px-5 rounded-full border border-transparent inline-flex items-center justify-center gap-1.5 text-[13.5px] font-bold text-white bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-lg shadow-genie-primary/30 transition-all hover:opacity-90 disabled:opacity-60"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? "Saving..." : "Save details"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
};
